export interface Coupon {
  id: string;
  tenant_id: string;
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  min_order_value?: number | null;
  expires_at?: string | null;
  marketplace: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CouponCreatePayload {
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  min_order_value?: number | null;
  expires_at?: string | null;
  marketplace?: string;
  is_active?: boolean;
}

export interface CouponUpdatePayload {
  code?: string;
  discount_type?: 'percent' | 'fixed';
  discount_value?: number;
  min_order_value?: number | null;
  expires_at?: string | null;
  marketplace?: string;
  is_active?: boolean;
}

export interface ApplyCouponPayload {
  coupon_id?: string;
  coupon_code?: string;
}

export interface ApplyCouponResult {
  success: boolean;
  message: string;
  original_price?: number | null;
  promotional_price?: number | null;
  discount_amount: number;
  coupon_code: string;
  formatted_copy?: string | null;
  item_id?: string | null;
  offer_id?: string | null;
}

const getApiBase = () => {
  return '';
};

export const fetchCoupons = async (
  token: string,
  filters?: { marketplace?: string; is_active?: boolean; search?: string }
): Promise<Coupon[]> => {
  const params = new URLSearchParams();
  if (filters?.marketplace && filters.marketplace !== 'all') {
    params.set('marketplace', filters.marketplace);
  }
  if (filters?.is_active !== undefined) {
    params.set('is_active', String(filters.is_active));
  }
  if (filters?.search) {
    params.set('search', filters.search);
  }

  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${getApiBase()}/api/v1/coupons${qs}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao buscar cupons de desconto.');
  }

  return res.json();
};

export const createCoupon = async (
  token: string,
  payload: CouponCreatePayload
): Promise<Coupon> => {
  const res = await fetch(`${getApiBase()}/api/v1/coupons`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao cadastrar cupom.');
  }

  return res.json();
};

export const updateCoupon = async (
  token: string,
  couponId: string,
  payload: CouponUpdatePayload
): Promise<Coupon> => {
  const res = await fetch(`${getApiBase()}/api/v1/coupons/${couponId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao atualizar cupom.');
  }

  return res.json();
};

export const deleteCoupon = async (
  token: string,
  couponId: string
): Promise<void> => {
  const res = await fetch(`${getApiBase()}/api/v1/coupons/${couponId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao excluir cupom.');
  }
};

export const applyCouponToOffer = async (
  token: string,
  offerId: string,
  payload: ApplyCouponPayload
): Promise<ApplyCouponResult> => {
  const res = await fetch(
    `${getApiBase()}/api/offers/${offerId}/apply-coupon`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Falha ao aplicar cupom na oferta.');
  }

  return res.json();
};

export const applyCouponToQueueItem = async (
  token: string,
  itemId: string,
  payload: ApplyCouponPayload
): Promise<ApplyCouponResult> => {
  const res = await fetch(
    `${getApiBase()}/api/v1/queue/${itemId}/apply-coupon`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Falha ao aplicar cupom no item da fila.'
    );
  }

  return res.json();
};
